/**
 * NarrationPlayer — cross-platform MP3 playback for pre-generated narration.
 *
 * Web: uses HTMLAudioElement with URL or data URI.
 * Native (Android): uses FTUEAudioPlayerModule (MediaPlayer) with HTTP URL.
 *
 * Audio is served from the backend via GET /audio/:sessionId/:audioId,
 * so both platforms just need a URL to play.
 */

import { Platform, TurboModuleRegistry } from "react-native";
import type { Spec } from "../../specs/NativeFTUEAudioPlayer";

const FTUEAudioPlayerModule = Platform.OS !== "web"
  ? TurboModuleRegistry.get<Spec>("FTUEAudioPlayerModule")
  : null;

export interface NarrationPlayerHandle {
  play(audioSrc: string): Promise<void>;
  pause(): void;
  resume(): void;
  stop(): void;
  isPlaying(): boolean;
}

class WebNarrationPlayer implements NarrationPlayerHandle {
  private audio: HTMLAudioElement | null = null;
  private playing = false;
  private rejectCurrent: ((err: Error) => void) | null = null;

  play(audioSrc: string): Promise<void> {
    this.stop();
    return new Promise((resolve, reject) => {
      this.rejectCurrent = reject;
      const audio = new Audio(audioSrc);
      this.audio = audio;
      this.playing = true;
      audio.onended = () => {
        this.playing = false;
        this.audio = null;
        this.rejectCurrent = null;
        resolve();
      };
      audio.onerror = () => {
        this.playing = false;
        this.audio = null;
        this.rejectCurrent = null;
        reject(new Error("Audio playback failed"));
      };
      audio.play().catch((err) => {
        this.playing = false;
        this.audio = null;
        this.rejectCurrent = null;
        reject(err);
      });
    });
  }

  pause() {
    if (this.audio) {
      this.audio.pause();
      this.playing = false;
    }
  }

  resume() {
    if (this.audio) {
      this.audio.play().catch(() => { this.playing = false; });
      this.playing = true;
    }
  }

  stop() {
    if (this.audio) {
      this.audio.pause();
      this.audio.onended = null;
      this.audio.onerror = null;
      this.audio = null;
    }
    this.playing = false;
    if (this.rejectCurrent) {
      this.rejectCurrent(new Error("stopped"));
      this.rejectCurrent = null;
    }
  }

  isPlaying() { return this.playing; }
}

class NativeNarrationPlayer implements NarrationPlayerHandle {
  private playing = false;
  private playGeneration = 0;

  async play(audioSrc: string): Promise<void> {
    if (!FTUEAudioPlayerModule) return;
    this.stop();
    const gen = ++this.playGeneration;
    this.playing = true;
    try {
      await FTUEAudioPlayerModule.play(audioSrc);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (!msg.includes("CANCELLED") && !msg.includes("stopped")) {
        console.warn("NarrationPlayer: playback failed", msg);
      }
    }
    if (this.playGeneration === gen) {
      this.playing = false;
    }
  }

  pause() {
    if (FTUEAudioPlayerModule?.pause) {
      FTUEAudioPlayerModule.pause();
    }
    this.playing = false;
  }

  resume() {
    if (FTUEAudioPlayerModule?.resume) {
      FTUEAudioPlayerModule.resume();
      this.playing = true;
    }
  }

  stop() {
    ++this.playGeneration;
    if (FTUEAudioPlayerModule) {
      FTUEAudioPlayerModule.stop();
    }
    this.playing = false;
  }

  isPlaying() { return this.playing; }
}

export function createNarrationPlayer(): NarrationPlayerHandle {
  return Platform.OS === "web" ? new WebNarrationPlayer() : new NativeNarrationPlayer();
}
